const formatterCache = new Map();

const getLocaleFromLanguage = language => {
  if (typeof language === "string" && language.toLowerCase().startsWith("ar")) {
    return "ar-EG-u-nu-arab";
  }
  return "en-US";
};

const EASTERN_ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

export const toArabicIndicDigitsInString = input => {
  if (input === null || input === undefined) {
    return "";
  }
  return String(input).replace(/\d/g, ch => EASTERN_ARABIC_DIGITS[Number(ch)] ?? ch);
};

/**
 * Inverse of `toArabicIndicDigitsInString`. Needed when a value the API already localized
 * has to be re-parsed client-side (e.g. matching "٥m ٣٠s" against a numeric pattern).
 */
export const toWesternDigitsInString = input => {
  if (input === null || input === undefined) {
    return "";
  }
  return String(input).replace(/[٠-٩۰-۹]/g, ch => {
    const code = ch.charCodeAt(0);
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
};

// LEFT-TO-RIGHT ISOLATE / POP DIRECTIONAL ISOLATE — a numeral run (thousands
// separators, a leading/trailing "-" or "%") is internally LTR even in Eastern
// Arabic-Indic digits, but dropping it into an RTL paragraph with no isolation
// lets the UAX #9 bidi algorithm reorder the group or strand the sign/percent
// on the wrong side. Wrapping keeps the run intact regardless of what
// surrounding Arabic text it lands next to.
// Written as escapes, not raw control characters, so the pair stays visible in diffs/review.
const LTR_ISOLATE_START = String.fromCharCode(0x2066); // LEFT-TO-RIGHT ISOLATE
const LTR_ISOLATE_END = String.fromCharCode(0x2069); // POP DIRECTIONAL ISOLATE

export const wrapBidiIsolate = value => `${LTR_ISOLATE_START}${value}${LTR_ISOLATE_END}`;

/**
 * Removes LRI/PDI bidi isolate marks from a string. Needed before handing text to
 * consumers that don't understand these invisible Unicode control characters and
 * render them as visible placeholder glyphs instead of hiding them — notably Excel
 * reading a CSV export. Browser UI should keep the wrapper; only export/CSV paths
 * need this.
 */
export const stripBidiIsolateMarks = value =>
  String(value ?? "").replace(new RegExp(`[${LTR_ISOLATE_START}${LTR_ISOLATE_END}]`, "g"), "");

const NUMERIC_RUN_REGEX = /[+-]?[\d٠-٩۰-۹]+(?:[.,:/-][\d٠-٩۰-۹]+)*%?/g;

export const isolateNumericRuns = (value, language) => {
  if (value === null || value === undefined || !isArabicLanguage(language)) {
    return value;
  }
  return String(value).replace(NUMERIC_RUN_REGEX, run => wrapBidiIsolate(run));
};

const getCacheKey = (locale, options) => {
  if (!options || Object.keys(options).length === 0) {
    return `${locale}::default`;
  }

  const orderedOptions = Object.keys(options)
    .sort()
    .reduce((acc, key) => {
      acc[key] = options[key];
      return acc;
    }, {});

  return `${locale}::${JSON.stringify(orderedOptions)}`;
};

const getFormatter = (locale, options) => {
  const key = getCacheKey(locale, options);

  if (!formatterCache.has(key)) {
    formatterCache.set(key, new Intl.NumberFormat(locale, options));
  }

  return formatterCache.get(key);
};

export const isArabicLanguage = language =>
  typeof language === "string" && language.toLowerCase().startsWith("ar");

/**
 * Locale-grouped digits *without* the bidi isolate wrapper. Use this when a unit has to be
 * appended to the number (`%`, `x`, `K`) — the wrapper has to enclose the whole token,
 * otherwise the unit is left outside the isolate and the bidi algorithm can strand it on
 * the wrong side of the digits. Wrap the composed token with `wrapBidiIsolate` afterwards.
 */
export const formatNumberDigits = (value, options = {}, language = "en") => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const locale = getLocaleFromLanguage(language);

  if (typeof value === "bigint") {
    return getFormatter(locale, options).format(value);
  }

  const numericValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return getFormatter(locale, options).format(numericValue);
};

export const formatNumber = (value, options = {}, language = "en") => {
  const formatted = formatNumberDigits(value, options, language);
  if (formatted === "" || !isArabicLanguage(language)) {
    return formatted;
  }
  return wrapBidiIsolate(formatted);
};

export const getNumberLocale = language => getLocaleFromLanguage(language);
